import { Component, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BreakpointObserver, BreakpointState, Breakpoints } from '@angular/cdk/layout';
import { Router } from '@angular/router';
import { MatSort, SortDirection } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

import { ColumnFilter, RequestData, SearchSort } from 'src/app/common/models/request-data.model';
import { FilterListItemModel, SearchFilterComponent, SearchParam } from 'src/app/common/components/search-filter/search-filter.component';
import { ConfirmDialogComponent, ConfirmDialogModel } from 'src/app/common/components/confirm-dialog/confirm-dialog.component';
import { SnackBarData } from 'src/app/common/components/snackbar/model/snackbar.model';

import { StateManagerService } from 'src/app/common/services/state.manager.service';
import { PermissionService } from 'src/app/auth/permission.service';
import { SnackBarService } from 'src/app/common/components/snackbar/snackbar.service';

import { AdsSettingsService } from './ads-settings-service.service';

export interface AdsModel {
  id: number;
  title: string;
  is_active: number;
  period_from: string;
  period_to: string;
  is_default: number;
  is_deleted: number;
  media_type: string;
}

@Component({
  selector: 'app-ads-settings',
  templateUrl: './ads-settings.component.html',
  styleUrls: ['./ads-settings.component.scss'],
  standalone: false
})
export class AdsSettingsComponent {
  adsList: AdsModel[] = [];
  isLoading = true;
  filter: FilterListItemModel[] = [
    { title: "Title", column: "title" },
    { title: "Media Type", column: "media_type" }
  ];

  displayedColumns = ['title', 'media_type', 'period_from', 'period_to', 'status', 'action'];
  pagingData = { length: 0, pageSize: 50, pageIndex: 0 };

  isHandset: boolean = false;
  searchColumFilter: SearchParam = {};
  columnSortData: SearchSort[] = [];
  selectedItem?: AdsModel;
  dataSource = new MatTableDataSource(this.adsList);
  permission: any;

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('searchFilter') searchFilter!: SearchFilterComponent;

  constructor(
    private breakpointObserver: BreakpointObserver,
    private adsSettingsService: AdsSettingsService,
    public dialog: MatDialog,
    private snackBarService: SnackBarService,
    private permissionService: PermissionService,
    private router: Router,
    private stateManagerService: StateManagerService,
  ) {
    this.permission = permissionService.getPagePermission("utilities");
  }

  ngOnInit(): void {
    this.breakpointObserver.observe([Breakpoints.Handset]).subscribe((state: BreakpointState) => {
      this.isHandset = state.matches;
    });
    this.isLoading = false;
  }

  ngAfterViewInit(): void {
    this.loadPreviousState();
  }

  setDataSource(list: AdsModel[]) {
    this.dataSource = new MatTableDataSource(list);
    this.dataSource.sort = this.sort;
  }

  loadPreviousState(): void {
    var previousState = this.stateManagerService.getState(this.constructor.name);

    if (previousState !== undefined && this.searchFilter != undefined) {
      if (previousState.state.search.filters.scope) {
        this.pagingData.pageIndex = previousState.state.search.filters.scope.offset / previousState.state.search.filters.scope.limit;
        this.pagingData.pageSize = previousState.state.search.filters.scope.limit;
      }

      this.searchColumFilter = JSON.parse(JSON.stringify(previousState.state.search.filters));
      this.searchFilter.setSearchFilter(this.searchColumFilter, false);

      if (previousState.state.search.sort != undefined && previousState.state.search.sort.length > 0) {
        // This will emit matSortChange -> sortData() -> loadData()
        this.setSortColumn(previousState.state.search.sort[0].column, previousState.state.search.sort[0].order);
      } else {
        this.loadData();
      }

    } else {
      // This will emit matSortChange -> sortData() -> loadData()
      this.setSortColumn('id', 'desc');
    }
  }

  setSortColumn(column: string, direction: SortDirection): void {
    this.columnSortData.length = 0;
    this.columnSortData.push({ column: column, order: direction });
    this.sort.sort({ id: column, start: direction, disableClear: false });
  }

  getSort(): SearchSort[] {
    return this.columnSortData;
  }

  buildRequestData(): RequestData {
    let searchParam = this.getSearchParam();
    let requestData: RequestData = {
      search: {
        filters: {
          simpleSearch: searchParam.freeSearch,
          advSearch: searchParam.advSearch || [],
          scope: {
            limit: this.pagingData.pageSize,
            offset: (this.pagingData.pageIndex * this.pagingData.pageSize),
          }
        },
        sort: this.getSort(),
      }
    };
    return requestData;
  }

  onFilterApplied(columFilter: any): void {
    this.searchColumFilter = columFilter;
    this.loadData();
  }

  loadData(): void {
    this.isLoading = true;
    let requestData = this.buildRequestData();

    this.adsSettingsService.getList(requestData).subscribe(response => {
      if (response.status == 'SUCCESS') {
        this.adsList = response.data.ads || [];
        this.setDataSource(this.adsList);
        this.pagingData.length = response.data.total_count || 0;

        if (this.pagingData.length == 0) {
          let snackBarData: SnackBarData = {
            message: 'No data found. Please remove or change the filter if any.',
            title: 'No Data!!!',
            type: 'warn'
          }
          this.snackBarService.openSnackBar(snackBarData);
          this.selectedItem = undefined;
        } else {
          this.onRowSelected(this.adsList[0])
          this.stateManagerService.setState(this.constructor.name, requestData);
        }
      } else {
        let snackBarData: SnackBarData = {
          message: response.message,
          title: 'Failed!!!',
          type: 'error'
        }
        this.snackBarService.openSnackBar(snackBarData);
      }
      this.isLoading = false;
    });
  }

  onRowSelected(adInfo: AdsModel): void {
    this.selectedItem = adInfo;
  }

  sortData($event: any): void {
    var sd = this.sort;
    this.columnSortData.length = 0;
    this.columnSortData.push({ column: sd.active, order: sd.direction });
    this.loadData();
  }

  getSearchParam(): SearchParam {
    return this.searchColumFilter;
  }

  onEdit(adInfo: AdsModel): void {
    this.router.navigate(['utilities/ads-settings-edit', adInfo.id]);
  }

  onDelete(adInfo: AdsModel): void {
    const dialogData = new ConfirmDialogModel("Confirm Action", `Are you sure you want to delete the ad '${adInfo.title}'?`);
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      maxWidth: "400px",
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        this.adsSettingsService.deleteAd(adInfo.id).subscribe(res => {
          if (res.status === 'SUCCESS') {
            this.snackBarService.openSnackBar({
              title: 'Deleted',
              message: 'Ad deleted successfully.',
              type: 'success'
            });
            this.loadData();
          } else {
            this.snackBarService.openSnackBar({
              title: 'Error',
              message: res.message || 'Could not delete ad.',
              type: 'error'
            });
          }
        });
      }
    });
  }

  onePageEvent(event: any): void {
    this.pagingData.pageIndex = event.pageIndex;
    this.pagingData.pageSize = event.pageSize;
    this.loadData();
  }

  addClicked(): void {
    this.router.navigate(['utilities/ads-settings-edit', 0]);
  }
}
